import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import Modal from './Modal';
import Pagination from './Pagination';
import LabelPrintModal from './LabelPrintModal';
import { printLaserSheet } from '../utils/qzTrayService';
import './StockAuditModal.css';

const QUICK_NOTE_CHIPS = [
  'Remnant waste',
  'Damaged end',
  'Mislabeled bolt',
  'Cutting allowance',
  'Recounted OK',
];

export function parseRackLocation(locStr) {
  if (!locStr || typeof locStr !== 'string') {
    return { line: '', rack: '', shelf: '' };
  }
  const clean = locStr.trim().replace(/^LOC:\s*/i, '');
  const parts = clean.split(/[-/,\s]+/).filter(Boolean);
  if (parts.length === 0) return { line: '', rack: '', shelf: '' };
  if (parts.length === 1) return { line: parts[0].toUpperCase(), rack: '', shelf: '' };
  if (parts.length === 2) return { line: parts[0].toUpperCase(), rack: parts[1].toUpperCase(), shelf: '' };
  return {
    line: parts[0].toUpperCase(),
    rack: parts[1].toUpperCase(),
    shelf: parts[2].toUpperCase(),
  };
}

export function composeRackLocation(line, rack, shelf) {
  const l = (line || '').trim().toUpperCase();
  const r = (rack || '').trim().toUpperCase();
  const s = (shelf || '').trim().toUpperCase();
  const parts = [];
  if (l) parts.push(l);
  if (r) parts.push(r);
  if (s) parts.push(s);
  return parts.join('-');
}

export default function StockAuditTabView({ apiBase = '', onAuditSaved }) {
  const { currentUser, canPerformAction, canAccessPage } = useAuth();
  const isAdmin = !!currentUser?.customClaims?.admin || canAccessPage('admin');
  const [showLabelPrintModal, setShowLabelPrintModal] = useState(false);

  // Batches state
  const [batches, setBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState(() => localStorage.getItem('audit_last_batch_id') || '');
  const [activeBatch, setActiveBatch] = useState(null);
  const [isLoadingBatches, setIsLoadingBatches] = useState(false);
  const [batchesError, setBatchesError] = useState('');

  // Create Batch Form Modal state
  const [showCreateBatchModal, setShowCreateBatchModal] = useState(false);
  const [newBatchName, setNewBatchName] = useState('');
  const [newBatchDescription, setNewBatchDescription] = useState('');
  const [isCreatingBatch, setIsCreatingBatch] = useState(false);
  const [createBatchError, setCreateBatchError] = useState('');

  // SKUs in Batch state
  const [skus, setSkus] = useState([]);
  const [isLoadingSkus, setIsLoadingSkus] = useState(false);
  const [skusError, setSkusError] = useState('');
  
  // Fast Debounced Search state
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debounceTimerRef = useRef(null);

  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'pending' | 'counted' | 'discrepancy'
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 30;

  // SKU Counting Modal state
  const [countingSku, setCountingSku] = useState(null);
  const [isLoadingSkuDetails, setIsLoadingSkuDetails] = useState(false);
  const [skuDetailsError, setSkuDetailsError] = useState('');
  const [pieceInputs, setPieceInputs] = useState([]);
  const [lastUsedLine, setLastUsedLine] = useState(() => localStorage.getItem('audit_last_line') || '');
  const [lastUsedRack, setLastUsedRack] = useState(() => localStorage.getItem('audit_last_rack') || '');
  const [newPieceDraft, setNewPieceDraft] = useState({
    pieceId: '',
    length: '',
    rackLine: localStorage.getItem('audit_last_line') || '',
    rackNumber: localStorage.getItem('audit_last_rack') || '',
    shelfNumber: '',
  });
  const [auditNote, setAuditNote] = useState('');
  const [countStartedAt, setCountStartedAt] = useState(null);
  const [isSubmittingCount, setIsSubmittingCount] = useState(false);
  const [concurrencyConflict, setConcurrencyConflict] = useState(null);
  const [savedSummaryData, setSavedSummaryData] = useState(null);
  const [savingPieceId, setSavingPieceId] = useState(null);
  const [pieceAuditStatusMap, setPieceAuditStatusMap] = useState({});
  const [selectedPieceIds, setSelectedPieceIds] = useState(new Set());
  const [reprintingPieceId, setReprintingPieceId] = useState(null);
  const [isReprintingAll, setIsReprintingAll] = useState(false);
  const [reprintStatusMap, setReprintStatusMap] = useState({});

  // Fast token helper
  const tokenHeader = useCallback(async () => {
    if (!currentUser) return {};
    const token = await currentUser.getIdToken();
    return { Authorization: `Bearer ${token}` };
  }, [currentUser]);

  // Handle Search Input with 300ms Debounce
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setSearchQuery(val.trim());
      setCurrentPage(1);
    }, 300);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      setSearchQuery(searchInput.trim());
      setCurrentPage(1);
    }
  };

  const handleClearSearch = () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    setSearchInput('');
    setSearchQuery('');
    setCurrentPage(1);
  };

  // Fetch all audit batches
  const fetchBatches = useCallback(async () => {
    if (!currentUser) return;
    setIsLoadingBatches(true);
    setBatchesError('');
    try {
      const headers = await tokenHeader();
      const res = await fetch(`${apiBase}/api/v1/inventory/audit/batches`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to fetch audit batches.');

      const batchList = Array.isArray(data.batches) ? data.batches : [];
      setBatches(batchList);

      if (batchList.length > 0) {
        const savedId = localStorage.getItem('audit_last_batch_id');
        const matchSaved = savedId ? batchList.find((b) => (b.batchId || b.id) === savedId) : null;
        const targetBatch = matchSaved || batchList[0];
        const targetId = targetBatch.batchId || targetBatch.id;
        setSelectedBatchId(targetId);
        setActiveBatch(targetBatch);
        localStorage.setItem('audit_last_batch_id', targetId);
      } else {
        setSelectedBatchId('');
        setActiveBatch(null);
      }
    } catch (err) {
      setBatchesError(err.message || 'Error loading audit batches.');
    } finally {
      setIsLoadingBatches(false);
    }
  }, [apiBase, currentUser, tokenHeader]);

  // Fetch SKUs for selected batch
  const fetchBatchSkus = useCallback(async () => {
    if (!currentUser || !selectedBatchId) return;
    setIsLoadingSkus(true);
    setSkusError('');
    try {
      const headers = await tokenHeader();

      // Fetch active batch info
      const bRes = await fetch(`${apiBase}/api/v1/inventory/audit/batches/${encodeURIComponent(selectedBatchId)}`, { headers });
      const bData = await bRes.json();
      if (bRes.ok && bData.batch) {
        setActiveBatch(bData.batch);
      }

      // Fetch SKUs
      const params = new URLSearchParams({
        page: String(currentPage),
        limit: String(itemsPerPage),
        search: searchQuery,
        status: statusFilter,
      });

      const res = await fetch(`${apiBase}/api/v1/inventory/audit/batches/${encodeURIComponent(selectedBatchId)}/skus?${params.toString()}`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to fetch SKUs.');

      setSkus(Array.isArray(data.skus) ? data.skus : []);
      setTotalCount(Number(data.totalCount || 0));
    } catch (err) {
      setSkusError(err.message || 'Error fetching SKUs.');
    } finally {
      setIsLoadingSkus(false);
    }
  }, [apiBase, selectedBatchId, currentPage, itemsPerPage, searchQuery, statusFilter, currentUser, tokenHeader]);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  useEffect(() => {
    if (selectedBatchId) {
      fetchBatchSkus();
    }
  }, [selectedBatchId, currentPage, statusFilter, searchQuery, fetchBatchSkus]);

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  // Handle Create Batch
  const handleCreateBatch = async (e) => {
    e.preventDefault();
    if (!newBatchName.trim()) {
      setCreateBatchError('Please provide a batch name.');
      return;
    }

    setIsCreatingBatch(true);
    setCreateBatchError('');
    try {
      const headers = await tokenHeader();
      headers['Content-Type'] = 'application/json';

      const res = await fetch(`${apiBase}/api/v1/inventory/audit/batches`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: newBatchName.trim(),
          description: newBatchDescription.trim(),
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to create audit batch.');

      setShowCreateBatchModal(false);
      setNewBatchName('');
      setNewBatchDescription('');
      await fetchBatches();
      if (data.batch?.batchId) {
        setSelectedBatchId(data.batch.batchId);
        localStorage.setItem('audit_last_batch_id', data.batch.batchId);
      }
    } catch (err) {
      setCreateBatchError(err.message || 'Failed to create batch.');
    } finally {
      setIsCreatingBatch(false);
    }
  };

  // Complete Batch
  const handleCompleteBatch = async () => {
    if (!selectedBatchId) return;
    if (!window.confirm('Are you sure you want to mark this audit batch as completed?')) return;

    try {
      const headers = await tokenHeader();
      const res = await fetch(`${apiBase}/api/v1/inventory/audit/batches/${encodeURIComponent(selectedBatchId)}/complete`, {
        method: 'POST',
        headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to complete batch.');

      await fetchBatches();
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  // Export CSV
  const handleExportCsv = async () => {
    if (!selectedBatchId) return;
    try {
      const headers = await tokenHeader();
      const res = await fetch(`${apiBase}/api/v1/inventory/audit/batches/${encodeURIComponent(selectedBatchId)}/export`, { headers });
      if (!res.ok) throw new Error('Failed to download CSV export.');

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stock_audit_${selectedBatchId}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Export error: ${err.message}`);
    }
  };

  // Open counting modal for SKU
  const openCountDrawer = async (skuItem) => {
    setCountingSku(skuItem);
    setIsLoadingSkuDetails(true);
    setSkuDetailsError('');
    setPieceInputs([]);
    setAuditNote('');
    setCountStartedAt(Date.now());
    setConcurrencyConflict(null);
    setSavedSummaryData(null);
    setReprintStatusMap({});
    setSelectedPieceIds(new Set());
    setSavingPieceId(null);

    try {
      const headers = await tokenHeader();
      const res = await fetch(`${apiBase}/api/v1/inventory/audit/batches/${encodeURIComponent(selectedBatchId)}/sku/${encodeURIComponent(skuItem.sku)}`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to fetch piece details.');

      const pieces = Array.isArray(data.pieces) ? data.pieces : [];
      let latestLine = lastUsedLine;
      let latestRack = lastUsedRack;

      for (const p of pieces) {
        const parsed = parseRackLocation(p.location);
        if (parsed.line) latestLine = parsed.line;
        if (parsed.rack) latestRack = parsed.rack;
      }

      const initialStatusMap = {};
      for (const p of pieces) {
        if (p.stockAuditBatchId === selectedBatchId || (p.lastStockVerifiedAt && data.existingAuditRecord)) {
          initialStatusMap[p.pieceId] = {
            saved: true,
            at: p.lastStockVerifiedAt ? new Date(p.lastStockVerifiedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Verified',
          };
        }
      }
      setPieceAuditStatusMap(initialStatusMap);
      setSelectedPieceIds(new Set());

      setLastUsedLine(latestLine);
      setLastUsedRack(latestRack);

      setPieceInputs(
        pieces.map((p) => {
          const parsed = parseRackLocation(p.location);
          const line = parsed.line || latestLine || '';
          const rack = parsed.rack || latestRack || '';
          const shelf = parsed.shelf || '';
          return {
            pieceId: p.pieceId,
            length: String(p.length || 0),
            originalLength: p.length || 0,
            rackLine: line,
            rackNumber: rack,
            shelfNumber: shelf,
            location: composeRackLocation(line, rack, shelf),
            action: 'update',
          };
        })
      );

      setNewPieceDraft({
        pieceId: '',
        length: '',
        rackLine: latestLine,
        rackNumber: latestRack,
        shelfNumber: '',
      });
    } catch (err) {
      setSkuDetailsError(err.message || 'Failed to fetch piece details.');
    } finally {
      setIsLoadingSkuDetails(false);
    }
  };

  const handleCloseCountModal = () => {
    setCountingSku(null);
    setSavedSummaryData(null);
    setPieceInputs([]);
    setConcurrencyConflict(null);
    setReprintStatusMap({});
    setPieceAuditStatusMap({});
    setSelectedPieceIds(new Set());
    setSavingPieceId(null);
  };

  const handleEditRecount = () => {
    setSavedSummaryData(null);
  };

  const handleNextProduct = () => {
    if (!countingSku) return;
    const currentIdx = skus.findIndex((s) => s.sku === countingSku.sku);
    const nextPending = skus.slice(currentIdx + 1).find((s) => s.status === 'pending') ||
                        skus.find((s) => s.status === 'pending' && s.sku !== countingSku.sku);

    if (nextPending) {
      openCountDrawer(nextPending);
    } else {
      handleCloseCountModal();
    }
  };

  const handleReprint = async (targetPieces = null, pieceIdKey = 'ALL') => {
    if (!countingSku || !selectedBatchId) return;
    if (pieceIdKey === 'ALL') setIsReprintingAll(true);
    else setReprintingPieceId(pieceIdKey);

    try {
      const headers = await tokenHeader();
      headers['Content-Type'] = 'application/json';
      const body = targetPieces ? JSON.stringify({ pieces: targetPieces }) : JSON.stringify({});

      const res = await fetch(`${apiBase}/api/v1/inventory/audit/batches/${encodeURIComponent(selectedBatchId)}/sku/${encodeURIComponent(countingSku.sku)}/reprint`, {
        method: 'POST',
        headers,
        body,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to send reprint job.');

      setReprintStatusMap((prev) => ({ ...prev, [pieceIdKey]: 'Sent!' }));
      setTimeout(() => {
        setReprintStatusMap((prev) => ({ ...prev, [pieceIdKey]: null }));
      }, 2500);
    } catch (err) {
      alert(`Reprint error: ${err.message}`);
    } finally {
      setIsReprintingAll(false);
      setReprintingPieceId(null);
    }
  };

  // Print audited piece labels on A4 sticker sheet (ST-33 format)
  const handlePrintA4Sheet = (targetPieces = null) => {
    const piecesToPrint = targetPieces && targetPieces.length > 0
      ? targetPieces
      : (savedSummaryData?.pieces || []);

    if (!piecesToPrint || piecesToPrint.length === 0) {
      alert('No pieces to print on A4 sheet.');
      return;
    }

    const currentSkuName = countingSku?.sku || savedSummaryData?.sku || '';
    const dateStr = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    const batchName = activeBatch?.name || '';

    let startSlot = 1;
    const inputSlot = window.prompt('Enter starting sticker position on ST-33 A4 Sheet (1 - 33):', '1');
    if (inputSlot === null) return;
    const parsed = parseInt(inputSlot, 10);
    if (!isNaN(parsed) && parsed >= 1 && parsed <= 33) {
      startSlot = parsed;
    }

    const jobs = piecesToPrint.map((p) => {
      const pid = String(p.pieceId || '').trim();
      const len = Number(p.length) || 0;
      const loc = p.location || '';
      const line1 = loc ? `PIECE: ${pid} • LOC: ${loc}` : `PIECE: ${pid}`;
      const line2 = currentSkuName;
      const line3 = batchName ? `${len.toFixed(2)}m • ${batchName}` : `${len.toFixed(2)}m • [${dateStr}]`;
      return {
        payload: {
          line1,
          line2,
          line3,
          barcodeText: pid,
        },
        style: {
          textCase: 'as_typed',
          textAlign: 'center',
          barcodeAlign: 'center',
        },
      };
    });

    try {
      printLaserSheet({ jobs, startSlot });
    } catch (err) {
      alert(`A4 Sheet Print Error: ${err.message || err}`);
    }
  };

  // Calculate live counted stock
  const currentPreStock = useMemo(() => {
    return pieceInputs.reduce((sum, p) => {
      return p.action === 'add' ? sum : sum + (Number(p.originalLength) || 0);
    }, 0);
  }, [pieceInputs]);

  const liveCountedStock = useMemo(() => {
    return pieceInputs.reduce((sum, p) => {
      if (p.action === 'disable') return sum;
      const val = parseFloat(p.length);
      return sum + (isNaN(val) || val < 0 ? 0 : val);
    }, 0);
  }, [pieceInputs]);

  const liveDifference = liveCountedStock - currentPreStock;
  const isLargeDiscrepancy = Math.abs(liveDifference) > 2.0;

  // Handle piece input change
  const handlePieceLengthChange = (idx, value) => {
    const updated = [...pieceInputs];
    updated[idx].length = value;
    if (updated[idx].action === 'disable') {
      updated[idx].action = 'update';
    }
    setPieceInputs(updated);
  };

  // Handle 3-part rack field change (Line, Rack, Shelf)
  const handleRackFieldChange = (idx, field, rawValue) => {
    const value = rawValue.toUpperCase().trimStart();
    const updated = [...pieceInputs];
    updated[idx][field] = value;
    if (field === 'rackLine' && value) {
      setLastUsedLine(value);
      localStorage.setItem('audit_last_line', value);
    }
    if (field === 'rackNumber' && value) {
      setLastUsedRack(value);
      localStorage.setItem('audit_last_rack', value);
    }
    updated[idx].location = composeRackLocation(
      updated[idx].rackLine,
      updated[idx].rackNumber,
      updated[idx].shelfNumber
    );
    setPieceInputs(updated);
  };

  // Step length helper (+1m, +0.5m, -0.5m, -1m)
  const handleStepLength = (idx, step) => {
    const updated = [...pieceInputs];
    const current = parseFloat(updated[idx].length) || 0;
    const nextVal = Math.max(0, current + step);
    updated[idx].length = String(Number(nextVal.toFixed(2)));
    if (updated[idx].action === 'disable') updated[idx].action = 'update';
    setPieceInputs(updated);
  };

  // Quick Match System Length
  const handleMatchSystem = (idx) => {
    const updated = [...pieceInputs];
    updated[idx].length = String(updated[idx].originalLength || 0);
    updated[idx].action = 'update';
    setPieceInputs(updated);
  };

  // Toggle piece disabled/lost
  const handleTogglePieceDisabled = (idx) => {
    const updated = [...pieceInputs];
    if (updated[idx].action === 'disable') {
      updated[idx].action = 'update';
      updated[idx].length = String(updated[idx].originalLength || 0);
    } else {
      updated[idx].action = 'disable';
      updated[idx].length = '0';
    }
    setPieceInputs(updated);
  };

  // Add newly discovered piece
  const handleAddPiece = () => {
    if (!newPieceDraft.pieceId.trim() || !newPieceDraft.length) {
      alert('Please provide Piece ID and Length.');
      return;
    }
    const parsed = parseFloat(newPieceDraft.length);
    if (isNaN(parsed) || parsed <= 0) {
      alert('Length must be greater than 0.');
      return;
    }

    const composedLoc = composeRackLocation(
      newPieceDraft.rackLine,
      newPieceDraft.rackNumber,
      newPieceDraft.shelfNumber
    );

    const newPid = newPieceDraft.pieceId.trim().toUpperCase();

    setPieceInputs([
      ...pieceInputs,
      {
        pieceId: newPid,
        length: String(parsed),
        originalLength: 0,
        rackLine: newPieceDraft.rackLine,
        rackNumber: newPieceDraft.rackNumber,
        shelfNumber: newPieceDraft.shelfNumber,
        location: composedLoc,
        action: 'add',
      },
    ]);

    setSelectedPieceIds((prev) => new Set([...prev, newPid]));

    setNewPieceDraft({
      pieceId: '',
      length: '',
      rackLine: lastUsedLine,
      rackNumber: lastUsedRack,
      shelfNumber: '',
    });
  };

  // Submit physical count for selected pieces
  const handleSubmitCount = async (ignoreConflict = false) => {
    if (!countingSku || !selectedBatchId) return;

    const selectedPieces = pieceInputs.filter((p) => selectedPieceIds.has(p.pieceId));
    if (selectedPieces.length === 0) {
      alert('Please select at least one piece to save & print.');
      return;
    }

    if (isLargeDiscrepancy && !auditNote.trim()) {
      alert('A note is required for large discrepancies (> 2m) to document why the stock differs.');
      return;
    }

    setIsSubmittingCount(true);
    try {
      const headers = await tokenHeader();
      headers['Content-Type'] = 'application/json';

      const payload = {
        countStartedAt,
        pieces: selectedPieces.map((p) => ({
          pieceId: p.pieceId,
          action: p.action,
          length: parseFloat(p.length) || 0,
          location: p.location,
        })),
        note: auditNote.trim(),
        ignoreConcurrencyWarning: ignoreConflict,
      };

      const res = await fetch(`${apiBase}/api/v1/inventory/audit/batches/${encodeURIComponent(selectedBatchId)}/sku/${encodeURIComponent(countingSku.sku)}/count`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.status === 409 && data.concurrencyConflict) {
        setConcurrencyConflict(data);
        setIsSubmittingCount(false);
        return;
      }

      if (!res.ok) throw new Error(data.message || 'Failed to save count.');

      const activePiecesForSummary = selectedPieces
        .filter((p) => p.action !== 'disable' && parseFloat(p.length) > 0)
        .map((p) => ({
          pieceId: p.pieceId,
          length: parseFloat(p.length) || 0,
          location: p.location || '',
        }));

      // Mark selected pieces as audited
      const updatedStatus = { ...pieceAuditStatusMap };
      const currentTimeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      selectedPieces.forEach((p) => {
        updatedStatus[p.pieceId] = {
          saved: true,
          at: currentTimeStr,
          length: parseFloat(p.length) || 0,
          location: p.location || '',
        };
      });
      setPieceAuditStatusMap(updatedStatus);

      // Update originalLength so system baseline matches the counted pieces
      const selectedIdSet = new Set(selectedPieces.map((p) => p.pieceId));
      setPieceInputs((prev) =>
        prev.map((p) => (selectedIdSet.has(p.pieceId) ? { ...p, originalLength: parseFloat(p.length) || 0 } : p))
      );

      // Deselect saved pieces
      setSelectedPieceIds(new Set());

      // Show summary popup with option to reprint labels
      setSavedSummaryData({
        sku: countingSku.sku,
        preStock: currentPreStock,
        countedStock: liveCountedStock,
        difference: liveDifference,
        pieces: activePiecesForSummary,
        status: Math.abs(liveDifference) < 0.05 ? 'counted' : 'discrepancy',
        note: auditNote.trim(),
      });

      // Refresh list & stats in background
      await fetchBatchSkus();
      if (onAuditSaved) onAuditSaved();
    } catch (err) {
      alert(`Error saving count: ${err.message}`);
    } finally {
      setIsSubmittingCount(false);
    }
  };

  // Submit count for an individual piece and immediately print 1 label
  const handleSaveSinglePiece = async (idx, ignoreConflict = false) => {
    const targetPiece = pieceInputs[idx];
    if (!targetPiece || !countingSku || !selectedBatchId) return;

    const pieceId = String(targetPiece.pieceId || '').trim();
    if (!pieceId) return;

    setSavingPieceId(pieceId);
    try {
      const headers = await tokenHeader();
      headers['Content-Type'] = 'application/json';

      const payload = {
        countStartedAt,
        pieces: [
          {
            pieceId: targetPiece.pieceId,
            action: targetPiece.action,
            length: parseFloat(targetPiece.length) || 0,
            location: targetPiece.location,
          },
        ],
        note: auditNote.trim() ? `${auditNote.trim()} [Piece: ${pieceId}]` : null,
        ignoreConcurrencyWarning: ignoreConflict,
      };

      const res = await fetch(
        `${apiBase}/api/v1/inventory/audit/batches/${encodeURIComponent(selectedBatchId)}/sku/${encodeURIComponent(countingSku.sku)}/count`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
        }
      );

      const data = await res.json();

      if (res.status === 409 && data.concurrencyConflict) {
        setConcurrencyConflict(data);
        setSavingPieceId(null);
        return;
      }

      if (!res.ok) throw new Error(data.message || 'Failed to save piece.');

      // Mark piece as saved & printed in the piece status map
      setPieceAuditStatusMap((prev) => ({
        ...prev,
        [pieceId]: {
          saved: true,
          at: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          length: parseFloat(targetPiece.length) || 0,
          location: targetPiece.location || '',
        },
      }));

      // Deselect if it was checked
      setSelectedPieceIds((prev) => {
        const next = new Set(prev);
        next.delete(pieceId);
        return next;
      });

      // Update originalLength so system baseline matches this piece's new count
      setPieceInputs((prev) =>
        prev.map((p, i) => (i === idx ? { ...p, originalLength: parseFloat(p.length) || 0 } : p))
      );

      // Refresh list & stats in background
      await fetchBatchSkus();
      if (onAuditSaved) onAuditSaved();
    } catch (err) {
      alert(`Error saving piece ${pieceId}: ${err.message}`);
    } finally {
      setSavingPieceId(null);
    }
  };

  const countedPercent = activeBatch?.targetSkusCount
    ? Math.min(100, Math.round(((activeBatch.countedSkusCount || 0) / activeBatch.targetSkusCount) * 100))
    : 0;

  return (
    <div className="stock-audit-tab-container">
      {/* Top Header Card */}
      <div className="stock-audit-tab-header">
        <div className="stock-audit-tab-title">
          <h2>
            <span>📋</span> Stock Audit & Cycle Counting
          </h2>
          <p className="stock-audit-tab-subtitle">
            Verify physical bolt lengths on racks against system inventory and print barcode stickers.
          </p>
        </div>

        <div className="stock-audit-batch-controls">
          {batches.length > 0 && (
            <select
              className="stock-audit-batch-select"
              value={selectedBatchId}
              disabled={isLoadingBatches}
              onChange={(e) => {
                setSelectedBatchId(e.target.value);
                localStorage.setItem('audit_last_batch_id', e.target.value);
                setCurrentPage(1);
              }}
            >
              {batches.map((b) => (
                <option key={b.batchId || b.id} value={b.batchId || b.id}>
                  {b.name} ({b.status === 'completed' ? 'Completed' : 'In Progress'})
                </option>
              ))}
            </select>
          )}

          {isAdmin && (
            <button className="stock-audit-btn-primary" onClick={() => setShowCreateBatchModal(true)}>
              + New Audit Batch
            </button>
          )}

          {isAdmin && activeBatch?.status === 'in_progress' && (
            <button className="stock-audit-btn-secondary" onClick={handleCompleteBatch}>
              Complete Batch
            </button>
          )}

          {activeBatch?.status === 'completed' && (
            <span style={{ fontSize: '0.8rem', fontWeight: '700', background: '#f1f5f9', color: '#475569', padding: '5px 10px', borderRadius: '6px', border: '1px solid #cbd5e1' }}>
              🔒 Completed
            </span>
          )}

          <button className="stock-audit-btn-secondary" onClick={handleExportCsv} title="Export Audit CSV">
            📥 Export CSV
          </button>

          <button
            className="stock-audit-btn-secondary"
            onClick={() => window.open('/stock-audit-mobile', '_blank')}
            title="Open Mobile Scanner"
            style={{ background: '#0284c7', color: '#fff', borderColor: '#0284c7' }}
          >
            📱 Mobile Scanner
          </button>

          {canPerformAction('inventory.printLabels') && (
            <button
              type="button"
              className="stock-audit-btn-primary"
              onClick={() => setShowLabelPrintModal(true)}
              style={{ background: '#7c3aed', color: '#fff', fontWeight: '600' }}
            >
              🖨️ Print Labels
            </button>
          )}
        </div>
      </div>

      {batchesError && <div className="error-message" style={{ margin: '10px 0' }}>{batchesError}</div>}

      {/* Progress & Summary Bar */}
      {activeBatch && (
        <div className="stock-audit-stats-card">
          <div className="stock-audit-stats-row">
            <div className="stock-audit-stat">
              <span className="stat-label">Total SKUs</span>
              <span className="stat-value">{activeBatch.targetSkusCount || totalCount || 0}</span>
            </div>
            <div className="stock-audit-stat">
              <span className="stat-label">Counted</span>
              <span className="stat-value text-green">{activeBatch.countedSkusCount || 0}</span>
            </div>
            <div className="stock-audit-stat">
              <span className="stat-label">Discrepancies</span>
              <span className="stat-value text-red">{activeBatch.discrepancyCount || 0}</span>
            </div>
            <div className="stock-audit-stat">
              <span className="stat-label">Net Variance</span>
              <span className={`stat-value ${(activeBatch.netQuantityDelta || 0) < 0 ? 'text-red' : (activeBatch.netQuantityDelta || 0) > 0 ? 'text-green' : ''}`}>
                {(activeBatch.netQuantityDelta || 0) > 0 ? `+${activeBatch.netQuantityDelta}m` : `${activeBatch.netQuantityDelta || 0}m`}
              </span>
            </div>
          </div>
          <div className="stock-audit-progress-bar-wrap">
            <div className="stock-audit-progress-bar" style={{ width: `${countedPercent}%` }} />
            <span className="stock-audit-progress-text">{countedPercent}% Audited</span>
          </div>
        </div>
      )}

      {/* Fast Filter Bar */}
      <div className="stock-audit-filter-bar">
        <div className="stock-audit-search-wrap">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Fast search SKU or Rack (e.g. POCO, A-01)..."
            value={searchInput}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            className="stock-audit-search-input"
          />
          {searchInput && (
            <button
              type="button"
              className="stock-audit-clear-search-btn"
              onClick={handleClearSearch}
              title="Clear search"
            >
              &times;
            </button>
          )}
        </div>

        {/* Status Filter Chips */}
        <div className="stock-audit-status-tabs">
          <button
            className={`status-tab ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => { setStatusFilter('all'); setCurrentPage(1); }}
          >
            All
          </button>
          <button
            className={`status-tab ${statusFilter === 'pending' ? 'active' : ''}`}
            onClick={() => { setStatusFilter('pending'); setCurrentPage(1); }}
          >
            Pending
          </button>
          <button
            className={`status-tab ${statusFilter === 'counted' ? 'active' : ''}`}
            onClick={() => { setStatusFilter('counted'); setCurrentPage(1); }}
          >
            Counted
          </button>
          <button
            className={`status-tab ${statusFilter === 'discrepancy' ? 'active' : ''}`}
            onClick={() => { setStatusFilter('discrepancy'); setCurrentPage(1); }}
          >
            Discrepancies
          </button>
        </div>
      </div>

      {/* SKU Table View */}
      <div className="stock-audit-table-wrap">
        {isLoadingSkus ? (
          <div className="stock-audit-loading">Loading audit records...</div>
        ) : skusError ? (
          <div className="error-message">{skusError}</div>
        ) : skus.length === 0 ? (
          <div className="stock-audit-empty">
            {searchQuery || statusFilter !== 'all'
              ? 'No SKUs found matching your filters.'
              : 'No SKUs in this batch. Select another batch or create a new one.'}
          </div>
        ) : (
          <table className="stock-audit-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>SKU</th>
                <th>Rack Location</th>
                <th>System Stock</th>
                <th>Physical Counted</th>
                <th>Variance</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {skus.map((item) => {
                const isCounted = item.isCounted;
                const discrepancy = isCounted ? Number(item.discrepancyQty || 0) : 0;
                const hasDiscrepancy = isCounted && Math.abs(discrepancy) > 0.05;

                return (
                  <tr
                    key={item.sku}
                    className={`stock-audit-row ${isCounted ? (hasDiscrepancy ? 'row-discrepancy' : 'row-matched') : ''}`}
                  >
                    <td>
                      {isCounted ? (
                        hasDiscrepancy ? (
                          <span className="stock-audit-badge badge-discrepancy">
                            ⚠️ {discrepancy > 0 ? `+${discrepancy}m` : `${discrepancy}m`}
                          </span>
                        ) : (
                          <span className="stock-audit-badge badge-matched">✓ Match</span>
                        )
                      ) : (
                        <span className="stock-audit-badge badge-pending">Pending</span>
                      )}
                    </td>
                    <td className="sku-cell">
                      <strong>{item.sku}</strong>
                    </td>
                    <td>
                      {item.locations && item.locations.length > 0 ? (
                        <div className="stock-audit-locations-list">
                          {item.locations.map((loc, i) => (
                            <span key={i} className="location-chip">📍 {loc}</span>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>No rack</span>
                      )}
                    </td>
                    <td>
                      <span className="stock-val">{item.currentStock.toFixed(2)}m</span>
                      <small style={{ color: '#64748b', marginLeft: '4px' }}>({item.pieceCount} pcs)</small>
                    </td>
                    <td>
                      {isCounted ? (
                        <span className="stock-val font-bold">{Number(item.countedStock || 0).toFixed(2)}m</span>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>—</span>
                      )}
                    </td>
                    <td>
                      {isCounted ? (
                        <span className={`variance-val ${discrepancy > 0 ? 'text-green' : discrepancy < 0 ? 'text-red' : ''}`}>
                          {discrepancy > 0 ? `+${discrepancy.toFixed(2)}m` : `${discrepancy.toFixed(2)}m`}
                        </span>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>—</span>
                      )}
                    </td>
                    <td>
                      <button
                        className={`stock-audit-count-btn ${isCounted ? 'btn-recount' : 'btn-count'}`}
                        onClick={() => openCountDrawer(item)}
                      >
                        {isCounted ? '✏️ Recount' : '🔍 Count'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalCount > itemsPerPage && (
        <div style={{ marginTop: '16px' }}>
          <Pagination
            currentPage={currentPage}
            totalItems={totalCount}
            itemsPerPage={itemsPerPage}
            onPageChange={(p) => setCurrentPage(p)}
          />
        </div>
      )}

      {/* ── CREATE AUDIT BATCH MODAL ── */}
      {showCreateBatchModal && (
        <Modal isOpen={showCreateBatchModal} onClose={() => setShowCreateBatchModal(false)}>
          <div className="stock-audit-create-batch-form">
            <h3>Create New Stock Audit Batch</h3>
            <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '16px' }}>
              Create a batch for a cycle count (e.g., "Row A - March Audit" or "Quarterly Linen Audit").
            </p>
            {createBatchError && <div className="error-message">{createBatchError}</div>}
            <form onSubmit={handleCreateBatch}>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '4px' }}>
                  Batch Name *
                </label>
                <input
                  type="text"
                  placeholder="e.g. Velvet & Satin Cycle Count Q1"
                  value={newBatchName}
                  onChange={(e) => setNewBatchName(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                  required
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '4px' }}>
                  Description / Instructions (Optional)
                </label>
                <textarea
                  rows={3}
                  placeholder="e.g. Audit all active pieces in Line A and B racks..."
                  value={newBatchDescription}
                  onChange={(e) => setNewBatchDescription(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button
                  type="button"
                  className="stock-audit-btn-secondary"
                  onClick={() => setShowCreateBatchModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="stock-audit-btn-primary"
                  disabled={isCreatingBatch}
                >
                  {isCreatingBatch ? 'Creating...' : 'Create Batch'}
                </button>
              </div>
            </form>
          </div>
        </Modal>
      )}

      {/* ── COUNTING WORKBENCH MODAL (WHEN SKU IS CLICKED) ── */}
      {countingSku && (
        <>
          <div className="stock-audit-popup-overlay" onClick={handleCloseCountModal} />
          <div className="stock-audit-count-popup stock-audit-count-drawer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="stock-audit-count-popup-inner">
              {/* Popup Header */}
              <div className="stock-audit-popup-header">
                <div>
                  <small style={{ textTransform: 'uppercase', color: '#64748b', fontWeight: 'bold' }}>
                    {savedSummaryData ? 'AUDIT VERIFICATION & REPRINT' : 'STOCK AUDIT COUNTING'}
                  </small>
                  <h3>{countingSku.sku}</h3>
                  <div style={{ fontSize: '0.85rem', color: '#475569', marginTop: '2px' }}>
                    Racks: {countingSku.locations?.join(', ') || 'No rack assigned'}
                  </div>
                </div>
                <button className="modal-close-button" onClick={handleCloseCountModal} style={{ position: 'static' }}>
                  &times;
                </button>
              </div>

              {isLoadingSkuDetails ? (
                <div style={{ padding: '40px 24px', textAlign: 'center', color: '#64748b' }}>
                  Loading physical piece records...
                </div>
              ) : savedSummaryData ? (
                /* ── SUCCESS & REPRINT SUMMARY VIEW ── */
                <>
                  <div className="stock-audit-popup-body stock-audit-summary-body">
                    <div className="stock-audit-summary-banner">
                      <span className="stock-audit-summary-banner-icon">✅</span>
                      <div>
                        <h4 style={{ margin: 0, fontSize: '1.05rem', color: '#065f46' }}>
                          Count Saved & Print Job Queued!
                        </h4>
                        <p style={{ margin: '2px 0 0', fontSize: '0.85rem', color: '#047857' }}>
                          Sticker labels for {savedSummaryData.pieces.length} piece(s) were sent to the printer queue.
                        </p>
                      </div>
                    </div>

                    {/* Summary Metrics Box */}
                    <div className={`stock-audit-variance-box ${savedSummaryData.status === 'counted' ? 'match' : 'discrepancy'}`}>
                      <div>
                        <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Physical Counted Total</div>
                        <div style={{ fontSize: '1.35rem', fontWeight: '800', color: '#0f172a' }}>
                          {savedSummaryData.countedStock.toFixed(2)}m
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        {Math.abs(savedSummaryData.difference) < 0.05 ? (
                          <span className="stock-audit-status-pill match">✓ EXACT MATCH</span>
                        ) : savedSummaryData.difference > 0 ? (
                          <span className="stock-audit-status-pill over">+{savedSummaryData.difference.toFixed(2)}m OVER</span>
                        ) : (
                          <span className="stock-audit-status-pill short">{savedSummaryData.difference.toFixed(2)}m SHORT</span>
                        )}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Difference vs Sys ({savedSummaryData.preStock.toFixed(2)}m)</div>
                        <div
                          style={{
                            fontSize: '1.35rem',
                            fontWeight: '800',
                            color: savedSummaryData.difference > 0 ? '#16a34a' : savedSummaryData.difference < 0 ? '#dc2626' : '#64748b',
                          }}
                        >
                          {savedSummaryData.difference > 0 ? `+${savedSummaryData.difference.toFixed(2)}m` : `${savedSummaryData.difference.toFixed(2)}m`}
                        </div>
                      </div>
                    </div>

                    {/* Printed Labels Card List with Reprint Buttons */}
                    <div className="stock-audit-summary-pieces-section">
                      <div className="stock-audit-summary-pieces-header">
                        <span style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>
                          Printed Labels ({savedSummaryData.pieces.length} active pieces)
                        </span>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <button
                            type="button"
                            className="stock-audit-a4-btn"
                            disabled={savedSummaryData.pieces.length === 0}
                            onClick={() => handlePrintA4Sheet(savedSummaryData.pieces)}
                            title="Print all audited pieces on A4 sticker sheet (ST-33, 33 labels per sheet)"
                          >
                            📄 Print A4 Sheet
                          </button>
                          <button
                            type="button"
                            className="stock-audit-reprint-btn all"
                            disabled={isReprintingAll || savedSummaryData.pieces.length === 0}
                            onClick={() => handleReprint(savedSummaryData.pieces, 'ALL')}
                            title="Send all pieces to thermal label printer"
                          >
                            {isReprintingAll ? '⏳ Sending All...' : reprintStatusMap['ALL'] ? `✓ ${reprintStatusMap['ALL']}` : '🖨️ Thermal Reprint All'}
                          </button>
                        </div>
                      </div>

                      {savedSummaryData.pieces.length === 0 ? (
                        <div style={{ padding: '16px', textAlign: 'center', color: '#64748b', background: '#f8fafc', borderRadius: '8px' }}>
                          No active pieces remaining (all pieces disabled/empty).
                        </div>
                      ) : (
                        <div className="stock-audit-summary-pieces-grid">
                          {savedSummaryData.pieces.map((p) => {
                            const isPieceReprinting = reprintingPieceId === p.pieceId;
                            const pieceReprintStatus = reprintStatusMap[p.pieceId];
                            return (
                              <div key={p.pieceId} className="stock-audit-summary-piece-row">
                                <div className="stock-audit-summary-piece-info">
                                  <span className="stock-audit-summary-piece-id">🏷️ Piece #{p.pieceId}</span>
                                  <span className="stock-audit-summary-piece-len">{p.length}m</span>
                                  <span className="stock-audit-summary-piece-loc">📍 {p.location || 'No rack'}</span>
                                </div>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                  <button
                                    type="button"
                                    className="stock-audit-a4-btn-small"
                                    onClick={() => handlePrintA4Sheet([p])}
                                    title="Print on A4 sticker sheet"
                                  >
                                    📄 A4
                                  </button>
                                  <button
                                    type="button"
                                    className={`stock-audit-reprint-btn single ${pieceReprintStatus ? 'sent' : ''}`}
                                    disabled={isPieceReprinting}
                                    onClick={() => handleReprint([p], p.pieceId)}
                                    title="Reprint 50x25mm sticker on thermal printer"
                                  >
                                    {isPieceReprinting ? '⏳ Sending...' : pieceReprintStatus ? `✓ ${pieceReprintStatus}` : '🖨️ Thermal'}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Summary Footer Actions */}
                  <div className="stock-audit-popup-actions">
                    <button type="button" className="stock-audit-btn-secondary" onClick={handleEditRecount}>
                      ✏️ Edit / Recount
                    </button>
                    <button type="button" className="stock-audit-btn-secondary" onClick={handleCloseCountModal}>
                      ✕ Close
                    </button>
                    <button
                      type="button"
                      className="stock-audit-btn-primary"
                      style={{ flex: 1 }}
                      onClick={handleNextProduct}
                    >
                      ➡️ Next Product (SKU)
                    </button>
                  </div>
                </>
              ) : (
                /* ── COUNTING EDIT FORM VIEW ── */
                <>
                  <div className="stock-audit-popup-body">
                    {skuDetailsError && <div className="error-message">{skuDetailsError}</div>}

                    {/* Concurrency conflict banner */}
                    {concurrencyConflict && (
                      <div className="stock-audit-concurrency-alert">
                        <h4>⚠️ Concurrent Sales / Order Activity Detected</h4>
                        <p style={{ margin: '4px 0' }}>
                          The following order deductions occurred while you were counting:
                        </p>
                        <ul style={{ margin: '4px 0 8px 16px', padding: 0 }}>
                          {concurrencyConflict.conflictingSales?.map((s, idx) => (
                            <li key={idx}>
                              Order #{s.orderNumber || 'N/A'}: {s.change}m from Piece {s.pieceId} ({s.timestamp ? new Date(s.timestamp).toLocaleTimeString() : ''})
                            </li>
                          ))}
                        </ul>
                        <p style={{ margin: 0, fontWeight: '500' }}>
                          Did your physical count on the rack already exclude these items?
                        </p>
                        <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                          <button
                            type="button"
                            className="stock-audit-btn-primary"
                            style={{ fontSize: '0.8rem', padding: '4px 8px' }}
                            onClick={() => handleSubmitCount(true)}
                          >
                            Yes, Save My Physical Count
                          </button>
                          <button
                            type="button"
                            className="stock-audit-btn-secondary"
                            style={{ fontSize: '0.8rem', padding: '4px 8px' }}
                            onClick={() => setConcurrencyConflict(null)}
                          >
                            Let Me Re-Verify
                          </button>
                        </div>
                      </div>
                    )}

                    <div style={{ margin: '8px 0 12px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#1e293b' }}>
                          Active Pieces on System ({pieceInputs.length})
                        </h4>
                        {pieceInputs.length > 0 && activeBatch?.status !== 'completed' && (
                          <button
                            type="button"
                            className="stock-audit-select-toggle-btn"
                            onClick={() => {
                              const selectableIds = pieceInputs.filter((p) => p.action !== 'disable').map((p) => p.pieceId);
                              if (selectedPieceIds.size === selectableIds.length && selectableIds.length > 0) {
                                setSelectedPieceIds(new Set());
                              } else {
                                setSelectedPieceIds(new Set(selectableIds));
                              }
                            }}
                          >
                            {selectedPieceIds.size === pieceInputs.filter((p) => p.action !== 'disable').length && pieceInputs.length > 0
                              ? 'Deselect All'
                              : `Select All (${pieceInputs.length})`}
                          </button>
                        )}
                      </div>
                      <small style={{ color: '#64748b', fontWeight: 'normal' }}>0.25m steps supported</small>
                    </div>

                    {/* Pieces Cards List */}
                    {pieceInputs.map((p, idx) => {
                      const isPieceSaved = !!pieceAuditStatusMap[p.pieceId]?.saved;
                      const isThisPieceSaving = savingPieceId === p.pieceId;
                      const isThisPieceReprinting = reprintingPieceId === p.pieceId;
                      const pieceReprintStatus = reprintStatusMap[p.pieceId];
                      const isSelected = selectedPieceIds.has(p.pieceId);

                      return (
                        <div
                          key={p.pieceId}
                          className={`stock-audit-piece-card ${p.action === 'disable' ? 'disabled' : ''} ${isPieceSaved ? 'audited' : ''} ${isSelected ? 'selected' : ''}`}
                        >
                          <div className="stock-audit-piece-card-top">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <label className="stock-audit-piece-checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  disabled={p.action === 'disable' || activeBatch?.status === 'completed'}
                                  onChange={(e) => {
                                    const next = new Set(selectedPieceIds);
                                    if (e.target.checked) next.add(p.pieceId);
                                    else next.delete(p.pieceId);
                                    setSelectedPieceIds(next);
                                  }}
                                  className="stock-audit-piece-checkbox"
                                />
                                <span className="stock-audit-piece-card-id">
                                  🏷️ Piece #{p.pieceId}
                                  {p.action === 'add' && <span className="stock-audit-new-tag">[NEW]</span>}
                                </span>
                              </label>
                              {isPieceSaved && (
                                <span className="stock-audit-piece-saved-badge" title={`Audited at ${pieceAuditStatusMap[p.pieceId]?.at || ''}`}>
                                  ✓ Audited ({pieceAuditStatusMap[p.pieceId]?.at || ''})
                                </span>
                              )}
                            </div>
                            <span className="stock-audit-piece-card-sys">
                              Sys: {p.originalLength}m
                            </span>
                          </div>

                          {/* 3-Tier Rack Location: Line - Rack - Shelf */}
                          <div className="stock-audit-rack-triplet">
                            <span className="stock-audit-rack-label-prefix">📍 Location:</span>
                            <div className="stock-audit-rack-field">
                              <label>Line</label>
                              <input
                                type="text"
                                placeholder="e.g. A"
                                value={p.rackLine || ''}
                                disabled={p.action === 'disable'}
                                onChange={(e) => handleRackFieldChange(idx, 'rackLine', e.target.value)}
                                className="stock-audit-rack-input"
                              />
                            </div>
                            <span className="stock-audit-rack-sep">-</span>
                            <div className="stock-audit-rack-field">
                              <label>Rack</label>
                              <input
                                type="text"
                                placeholder="e.g. 03"
                                value={p.rackNumber || ''}
                                disabled={p.action === 'disable'}
                                onChange={(e) => handleRackFieldChange(idx, 'rackNumber', e.target.value)}
                                className="stock-audit-rack-input"
                              />
                            </div>
                            <span className="stock-audit-rack-sep">-</span>
                            <div className="stock-audit-rack-field">
                              <label>Shelf</label>
                              <input
                                type="text"
                                placeholder="e.g. 2"
                                value={p.shelfNumber || ''}
                                disabled={p.action === 'disable'}
                                onChange={(e) => handleRackFieldChange(idx, 'shelfNumber', e.target.value)}
                                className="stock-audit-rack-input"
                              />
                            </div>
                            {p.location && (
                              <div className="stock-audit-composed-badge" title="Standard combined location code">
                                📍 {p.location}
                              </div>
                            )}
                          </div>

                          {/* Rapid Step Buttons + Manual Input with 'm' Unit */}
                          <div className="stock-audit-piece-step-controls">
                            <button
                              type="button"
                              className="stock-audit-step-btn btn-minus"
                              disabled={p.action === 'disable'}
                              onClick={() => handleStepLength(idx, -1)}
                              title="Minus 1 meter"
                            >
                              -1
                            </button>
                            <button
                              type="button"
                              className="stock-audit-step-btn btn-minus step-half"
                              disabled={p.action === 'disable'}
                              onClick={() => handleStepLength(idx, -0.5)}
                              title="Minus 0.5 meter"
                            >
                              -0.5
                            </button>
                            <div className="stock-audit-step-input-wrap">
                              <input
                                type="number"
                                step="0.25"
                                min="0"
                                className="stock-audit-step-input"
                                value={p.length}
                                disabled={p.action === 'disable'}
                                onChange={(e) => handlePieceLengthChange(idx, e.target.value)}
                              />
                              <span className="stock-audit-step-unit">m</span>
                            </div>
                            <button
                              type="button"
                              className="stock-audit-step-btn btn-plus step-half"
                              disabled={p.action === 'disable'}
                              onClick={() => handleStepLength(idx, 0.5)}
                              title="Plus 0.5 meter"
                            >
                              +0.5
                            </button>
                            <button
                              type="button"
                              className="stock-audit-step-btn btn-plus"
                              disabled={p.action === 'disable'}
                              onClick={() => handleStepLength(idx, 1)}
                              title="Plus 1 meter"
                            >
                              +1
                            </button>
                          </div>

                          {/* Unified Single-Line Action Bar: Match | Empty | Save & Print | Reprint */}
                          <div className="stock-audit-piece-unified-actions">
                            <button
                              type="button"
                              className="stock-audit-quick-btn match"
                              disabled={p.action === 'disable'}
                              onClick={() => handleMatchSystem(idx)}
                              title={`Set length to system length (${p.originalLength}m)`}
                            >
                              ✓ Match ({p.originalLength}m)
                            </button>
                            <button
                              type="button"
                              className={`stock-audit-quick-btn empty ${p.action === 'disable' ? 'is-disabled' : ''}`}
                              onClick={() => handleTogglePieceDisabled(idx)}
                              title={p.action === 'disable' ? 'Restore piece' : 'Mark piece as 0m / empty / lost'}
                            >
                              {p.action === 'disable' ? '↩ Undo' : '✕ Empty / Lost'}
                            </button>
                            <button
                              type="button"
                              className={`stock-audit-piece-save-btn ${isPieceSaved ? 'saved' : ''}`}
                              disabled={isThisPieceSaving || isSubmittingCount || activeBatch?.status === 'completed'}
                              onClick={() => handleSaveSinglePiece(idx)}
                              title={activeBatch?.status === 'completed' ? 'Batch is completed (Read-only)' : 'Save this piece & immediately print 1 label'}
                            >
                              {isThisPieceSaving
                                ? '⏳ Saving...'
                                : isPieceSaved
                                ? '✓ Saved (Save Again)'
                                : '💾 Save & Print'}
                            </button>
                            {isPieceSaved && (
                              <>
                                <button
                                  type="button"
                                  className="stock-audit-a4-btn-small"
                                  onClick={() => handlePrintA4Sheet([p])}
                                  title="Print on A4 sticker sheet (ST-33)"
                                >
                                  📄 A4
                                </button>
                                <button
                                  type="button"
                                  className="stock-audit-piece-reprint-btn"
                                  disabled={isThisPieceReprinting}
                                  onClick={() => handleReprint([p], p.pieceId)}
                                  title="Reprint 50x25mm sticker for this piece on thermal printer"
                                >
                                  {isThisPieceReprinting
                                    ? '⏳'
                                    : pieceReprintStatus
                                    ? `✓ ${pieceReprintStatus}`
                                    : '🖨️ Reprint'}
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {/* Add Unrecorded / Discovered Piece Card */}
                    <div className="stock-audit-found-piece-card">
                      <div className="stock-audit-found-piece-title">+ Found Unrecorded Piece on Shelf?</div>
                      <div className="stock-audit-found-piece-row1">
                        <input
                          type="text"
                          placeholder="Piece ID (e.g. 104)"
                          value={newPieceDraft.pieceId}
                          onChange={(e) => setNewPieceDraft({ ...newPieceDraft, pieceId: e.target.value })}
                          className="stock-audit-found-input flex-1"
                        />
                        <div className="stock-audit-found-len-wrap">
                          <input
                            type="number"
                            step="0.25"
                            placeholder="Meters (e.g. 5.5)"
                            value={newPieceDraft.length}
                            onChange={(e) => setNewPieceDraft({ ...newPieceDraft, length: e.target.value })}
                            className="stock-audit-found-input"
                          />
                          <span className="stock-audit-found-unit">m</span>
                        </div>
                      </div>
                      <div className="stock-audit-found-piece-row2">
                        <div className="stock-audit-found-loc-group">
                          <input
                            type="text"
                            placeholder="Line"
                            value={newPieceDraft.rackLine}
                            onChange={(e) => setNewPieceDraft({ ...newPieceDraft, rackLine: e.target.value.toUpperCase() })}
                            className="stock-audit-found-loc-input"
                          />
                          <span className="stock-audit-rack-sep" style={{ marginTop: 0 }}>-</span>
                          <input
                            type="text"
                            placeholder="Rack"
                            value={newPieceDraft.rackNumber}
                            onChange={(e) => setNewPieceDraft({ ...newPieceDraft, rackNumber: e.target.value.toUpperCase() })}
                            className="stock-audit-found-loc-input"
                          />
                          <span className="stock-audit-rack-sep" style={{ marginTop: 0 }}>-</span>
                          <input
                            type="text"
                            placeholder="Shelf"
                            value={newPieceDraft.shelfNumber}
                            onChange={(e) => setNewPieceDraft({ ...newPieceDraft, shelfNumber: e.target.value.toUpperCase() })}
                            className="stock-audit-found-loc-input"
                          />
                        </div>
                        <button
                          type="button"
                          className="stock-audit-btn-primary"
                          style={{ padding: '6px 14px', fontSize: '0.85rem' }}
                          onClick={handleAddPiece}
                        >
                          + Add Piece
                        </button>
                      </div>
                    </div>

                    {/* Variance Calculation Box with Status Badge */}
                    <div className={`stock-audit-variance-box ${Math.abs(liveDifference) < 0.05 ? 'match' : 'discrepancy'}`}>
                      <div>
                        <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Physical Counted Total</div>
                        <div style={{ fontSize: '1.35rem', fontWeight: '800', color: '#0f172a' }}>{liveCountedStock.toFixed(2)}m</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        {Math.abs(liveDifference) < 0.05 ? (
                          <span className="stock-audit-status-pill match">✓ EXACT MATCH</span>
                        ) : liveDifference > 0 ? (
                          <span className="stock-audit-status-pill over">+{liveDifference.toFixed(2)}m OVER</span>
                        ) : (
                          <span className="stock-audit-status-pill short">{liveDifference.toFixed(2)}m SHORT</span>
                        )}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Difference vs Sys ({currentPreStock.toFixed(2)}m)</div>
                        <div
                          style={{
                            fontSize: '1.35rem',
                            fontWeight: '800',
                            color: liveDifference > 0 ? '#16a34a' : liveDifference < 0 ? '#dc2626' : '#64748b',
                          }}
                        >
                          {liveDifference > 0 ? `+${liveDifference.toFixed(2)}m` : `${liveDifference.toFixed(2)}m`}
                        </div>
                      </div>
                    </div>

                    {/* Audit Note / Explanation with Quick Chips */}
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '6px', color: '#334155' }}>
                        Audit Note {isLargeDiscrepancy ? <span style={{ color: '#ef4444' }}>* (Required for &gt; 2m variance)</span> : '(Optional)'}
                      </label>
                      <div className="stock-audit-note-chips">
                        {QUICK_NOTE_CHIPS.map((chip) => (
                          <button
                            key={chip}
                            type="button"
                            className={`stock-audit-note-chip ${auditNote.includes(chip) ? 'selected' : ''}`}
                            onClick={() => setAuditNote(chip)}
                          >
                            {chip}
                          </button>
                        ))}
                      </div>
                      <textarea
                        rows={2}
                        placeholder="e.g. Cutting remnant waste, mislabeled bolt, physical recount verified..."
                        value={auditNote}
                        onChange={(e) => setAuditNote(e.target.value)}
                        style={{ width: '100%', padding: '8px', fontSize: '0.85rem', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>

                  {/* Action Buttons (Fixed Bottom Footer) */}
                  <div className="stock-audit-popup-actions">
                    <button type="button" className="stock-audit-btn-secondary" onClick={handleCloseCountModal}>
                      {activeBatch?.status === 'completed' ? 'Close' : 'Cancel'}
                    </button>
                    {activeBatch?.status === 'completed' ? (
                      <span style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9', color: '#64748b', borderRadius: '6px', fontWeight: '700', fontSize: '0.85rem', border: '1px solid #cbd5e1' }}>
                        🔒 Batch Completed (Read-Only)
                      </span>
                    ) : (
                      <button
                        type="button"
                        className={`stock-audit-btn-success ${selectedPieceIds.size === 0 ? 'btn-disabled-muted' : ''}`}
                        style={{ flex: 1 }}
                        disabled={isSubmittingCount || selectedPieceIds.size === 0}
                        onClick={() => handleSubmitCount(false)}
                      >
                        {isSubmittingCount
                          ? '⏳ Saving & Printing...'
                          : selectedPieceIds.size === 0
                          ? '💾 Select Pieces to Save & Print'
                          : `💾 Save & Print Selected (${selectedPieceIds.size} Piece${selectedPieceIds.size > 1 ? 's' : ''})`}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}

      <LabelPrintModal
        isOpen={showLabelPrintModal}
        onClose={() => setShowLabelPrintModal(false)}
        apiBase={apiBase}
        defaultAction="stock_audit"
      />
    </div>
  );
}
